import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  hospitalAdminApi,
  type MetaIntegrationConfig,
  type MetaRevenueValueMode,
  type MetaThmsAction,
} from "@/api/hospitalAdmin";
import { superAdminApi } from "@/api/superAdmin";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const metaActionLabels: Record<MetaThmsAction, string> = {
  CRM_LEAD_CREATED: "Qualified lead (Interested / Follow Up)",
  PATIENT_SELF_REGISTRATION: "Patient self registration",
  CALLBACK_REQUEST: "Contacted",
  APPOINTMENT_BOOKED: "Appointment booked",
  OPD_CONSULTATION_COMPLETED: "OPD consultation completed",
  DAY_CARE_REGISTRATION_COMPLETED: "Day care registration completed",
  IPD_ADMISSION_CREATED: "IPD admission created",
  SURGERY_SCHEDULED: "Surgery scheduled",
  SURGERY_COMPLETED: "Surgery completed",
  IPD_FINAL_BILL_FROZEN: "IPD final bill frozen",
  IPD_PAYMENT_RECEIVED: "IPD payment received",
  LOYALTY_APP_REGISTRATION: "Loyalty app registration",
};

export default function MetaIntegrationPage() {
  const qc = useQueryClient();
  const { isChainAdmin, user, activeHospitalId } = useAuth();
  const [hospitalId, setHospitalId] = useState("");
  const [metaEnabled, setMetaEnabled] = useState(false);
  const [datasetId, setDatasetId] = useState("");
  const [metaAccessToken, setMetaAccessToken] = useState("");
  const [testMode, setTestMode] = useState(false);
  const [metaTestCode, setMetaTestCode] = useState("");
  const [sendRevenueEvents, setSendRevenueEvents] = useState(true);
  const [revenueValueMode, setRevenueValueMode] = useState<MetaRevenueValueMode>("ACTUAL_COLLECTED");
  const [metaEventMapping, setMetaEventMapping] = useState<Partial<Record<MetaThmsAction, string>>>({});

  const { data: superHospitals = [], isLoading: hospitalsLoading } = useQuery({
    queryKey: ["super-admin-hospitals-meta"],
    queryFn: () => superAdminApi.getHospitals(),
    enabled: !isChainAdmin,
  });

  const hospitals = isChainAdmin ? user?.hospitals ?? [] : superHospitals;

  useEffect(() => {
    if (!hospitalId && activeHospitalId) setHospitalId(activeHospitalId);
  }, [activeHospitalId, hospitalId]);

  const { data: metaConfig, isLoading: metaLoading } = useQuery<MetaIntegrationConfig>({
    queryKey: ["meta-integration-config", hospitalId],
    queryFn: () => hospitalAdminApi.getMetaIntegrationConfig(hospitalId),
    enabled: !!hospitalId,
  });

  useEffect(() => {
    if (!metaConfig) return;
    setMetaEnabled(metaConfig.enabled);
    setDatasetId(metaConfig.datasetId ?? "");
    setTestMode(metaConfig.testMode ?? false);
    setMetaTestCode(metaConfig.testEventCode ?? "");
    setSendRevenueEvents(metaConfig.sendRevenueEvents ?? true);
    setRevenueValueMode(metaConfig.revenueValueMode ?? "ACTUAL_COLLECTED");
    setMetaEventMapping(metaConfig.eventMapping ?? {});
    setMetaAccessToken("");
  }, [metaConfig, hospitalId]);

  const saveMetaMutation = useMutation({
    mutationFn: () =>
      hospitalAdminApi.saveMetaIntegrationConfig(
        {
          enabled: metaEnabled,
          datasetId,
          testMode,
          sendRevenueEvents,
          revenueValueMode,
          ...(metaAccessToken.trim() ? { accessToken: metaAccessToken.trim() } : {}),
          testEventCode: metaTestCode,
          eventMapping: metaEventMapping,
        },
        hospitalId
      ),
    onSuccess: () => {
      toast.success("Meta integration saved for hospital");
      setMetaAccessToken("");
      qc.invalidateQueries({ queryKey: ["meta-integration-config", hospitalId] });
    },
    onError: (e: Error) => toast.error(e.message || "Failed to save Meta integration"),
  });

  const testEventMutation = useMutation({
    mutationFn: () => hospitalAdminApi.sendMetaTestEvent(hospitalId),
    onSuccess: (data) => {
      if (data.ok) toast.success("Test event sent to Meta");
      else toast.error(`Test event failed (HTTP ${data.status})`);
    },
    onError: (e: Error) => toast.error(e.message || "Test event failed"),
  });

  return (
    <div className="p-6 space-y-4 max-w-4xl">
      <div>
        <h1 className="text-2xl font-semibold">Meta Integration</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Configure per-hospital Dataset ID, access token, and THMS → Meta CRM funnel event mapping.
          Required for Instant Form &quot;Maximise number of qualified leads&quot; and Meta-attributed OT leads
          (website, WhatsApp, landing pages).
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Hospital</CardTitle>
        </CardHeader>
        <CardContent>
          <Label htmlFor="hospital">Select hospital</Label>
          <Select value={hospitalId} onValueChange={setHospitalId} disabled={hospitalsLoading}>
            <SelectTrigger id="hospital" className="mt-2 max-w-md">
              <SelectValue placeholder={hospitalsLoading ? "Loading…" : "Choose a hospital"} />
            </SelectTrigger>
            <SelectContent>
              {hospitals.map((h) => (
                <SelectItem key={h.id} value={h.id}>
                  {h.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {!hospitalId ? (
        <p className="text-sm text-muted-foreground">Select a hospital to configure Meta CAPI.</p>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Dataset &amp; access token</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-start gap-3">
              <Checkbox
                checked={metaEnabled}
                onCheckedChange={(v) => setMetaEnabled(!!v)}
                disabled={metaLoading || saveMetaMutation.isPending}
              />
              <div>
                <Label className="font-medium">Integration enabled</Label>
                <div className="text-sm text-muted-foreground mt-1">
                  Sends CRM Conversion Leads events (`event_source=crm`, `lead_event_source=THMS`) plus
                  Instant Form `lead_id` when available. Complete Events Manager → Connect CRM and map
                  funnel stages to these event names.
                  {metaConfig?.usingEnvFallback ? (
                    <span className="block mt-1 text-amber-700">
                      Server env vars are used until you save hospital-specific credentials.
                    </span>
                  ) : null}
                </div>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Checkbox
                checked={testMode}
                onCheckedChange={(v) => setTestMode(!!v)}
                disabled={metaLoading}
              />
              <div>
                <Label className="font-medium">Test mode</Label>
                <div className="text-sm text-muted-foreground mt-1">
                  Includes test event code on all outbound events when configured.
                </div>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Checkbox
                checked={sendRevenueEvents}
                onCheckedChange={(v) => setSendRevenueEvents(!!v)}
                disabled={metaLoading}
              />
              <div>
                <Label className="font-medium">Send revenue events</Label>
                <div className="text-sm text-muted-foreground mt-1">
                  Include value and currency on Purchase-family events.
                </div>
              </div>
            </div>

            <div className="grid gap-3 md:grid-cols-2">
              <div>
                <Label>Dataset ID</Label>
                <Input
                  value={datasetId}
                  onChange={(e) => setDatasetId(e.target.value)}
                  placeholder="Meta Dataset / Pixel ID"
                  disabled={metaLoading}
                />
              </div>
              <div>
                <Label>Access token</Label>
                <Input
                  type="password"
                  value={metaAccessToken}
                  onChange={(e) => setMetaAccessToken(e.target.value)}
                  placeholder={metaConfig?.hasAccessToken ? "•••••••• (leave blank to keep)" : "CAPI access token"}
                  disabled={metaLoading}
                />
              </div>
              <div>
                <Label>Test event code (optional)</Label>
                <Input
                  value={metaTestCode}
                  onChange={(e) => setMetaTestCode(e.target.value)}
                  placeholder="TEST12345"
                  disabled={metaLoading}
                />
              </div>
              <div>
                <Label>Purchase event value mode</Label>
                <Select
                  value={revenueValueMode}
                  onValueChange={(v) => setRevenueValueMode(v as MetaRevenueValueMode)}
                  disabled={metaLoading}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CONSULTATION_FEE">Consultation fee</SelectItem>
                    <SelectItem value="ADVANCE_RECEIVED">Advance amount received</SelectItem>
                    <SelectItem value="ACTUAL_COLLECTED">Actual revenue collected</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Event mapping (THMS action → Meta CRM funnel stage name)</Label>
              <p className="text-xs text-muted-foreground">
                Names must match Events Manager → Configure sales funnel. Recommended order:{" "}
                <code className="text-xs">Lead</code> → <code className="text-xs">Contact</code> →{" "}
                <code className="text-xs">Schedule</code> → <code className="text-xs">Purchase</code>{" "}
                (optimize for <code className="text-xs">Purchase</code> = OPD visited).{" "}
                <code className="text-xs">Lead</code> fires on CRM status Interested or Follow Up;{" "}
                <code className="text-xs">Contact</code> on Contacted. Outbound payloads always
                include <code className="text-xs">event_source=crm</code> and{" "}
                <code className="text-xs">lead_event_source=THMS</code>.
              </p>
              <div className="grid gap-2 md:grid-cols-2">
                {(Object.keys(metaActionLabels) as MetaThmsAction[]).map((key) => (
                  <div key={key} className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground min-w-[180px]">{metaActionLabels[key]}</span>
                    <Input
                      value={metaEventMapping[key] ?? metaConfig?.defaultEventMapping?.[key] ?? ""}
                      onChange={(e) =>
                        setMetaEventMapping((prev) => ({ ...prev, [key]: e.target.value }))
                      }
                      disabled={metaLoading}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-wrap justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  if (metaConfig?.defaultEventMapping) {
                    setMetaEventMapping({ ...metaConfig.defaultEventMapping });
                    toast.message("Mapping reset to CRM funnel defaults — click Save to apply");
                  }
                }}
                disabled={!hospitalId || metaLoading || !metaConfig?.defaultEventMapping}
              >
                Reset mapping defaults
              </Button>
              <Button
                variant="outline"
                onClick={() => testEventMutation.mutate()}
                disabled={!hospitalId || metaLoading || testEventMutation.isPending}
              >
                Send test event
              </Button>
              <Button
                onClick={() => saveMetaMutation.mutate()}
                disabled={metaLoading || saveMetaMutation.isPending}
              >
                Save for this hospital
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
